import type { TaskDefinition } from "@talent/protocol";

/**
 * The difficulty ladder: what happens to the level after a trial, and when a sitting ends.
 *
 * Pure and in its own module because it was copied into all ten renderers, and because it is the
 * one place where the pivot from measuring to teaching actually lives (ADR-011, R-5). Ten copies of
 * a policy is one policy and nine chances for it to drift, and the drift would be invisible: each
 * game would simply be a little more or less forgiving than the others, and nobody would know which
 * was intended.
 *
 * **What changed when the product changed.** The old rule ended the task after
 * `stopAfterErrors` consecutive errors. That is right for an instrument — pushing a child past
 * their limit records frustration as inability (v2 §19) — and wrong for an activity whose entire
 * purpose is the trial after the mistake. Now:
 *
 *   - `stepUpAfter` correct in a row raises the level, as before.
 *   - `stepDownAfter` errors in a row LOWERS it, rather than ending the sitting.
 *   - The sitting ends when `maxTrials` trials have been played, and essentially never otherwise.
 *
 * The consequence worth stating: a sitting can no longer end because a child was struggling. It
 * ends because it is over.
 */

export interface LadderState {
  difficulty: number;
  /** Correct answers in a row at the current level. */
  correctRun: number;
  /** Wrong answers in a row. */
  errorRun: number;
  /** Trials already completed, including the one just scored. */
  trialsDone: number;
}

export interface LadderStep extends LadderState {
  /**
   * A level change to record, or null.
   *
   * Returned rather than applied, because the renderer has to emit a `difficulty_change` event and
   * only it can await the recorder. Keeping the decision here and the writing there is what lets
   * this function stay pure.
   */
  change: { from: number; to: number; reason: "correct_streak" | "error" } | null;
  /** The sitting is over. */
  finished: boolean;
}

/**
 * The starting level for a run.
 *
 * Clamped into the task's own range, because the level comes from the server's suggestion — the top
 * of whatever objective the child is working on — and an objective's window is defined in another
 * file. A window that drifted past a ceiling would otherwise ask a generator for a level it refuses,
 * which surfaces as the game failing to open.
 */
export function startingDifficulty(task: TaskDefinition, startAt?: number): number {
  const { minDifficulty, maxDifficulty } = task.measurement;
  if (startAt === undefined) return minDifficulty;
  return Math.max(minDifficulty, Math.min(maxDifficulty, startAt));
}

export function newLadder(task: TaskDefinition, startAt?: number): LadderState {
  return {
    difficulty: startingDifficulty(task, startAt),
    correctRun: 0,
    errorRun: 0,
    trialsDone: 0,
  };
}

export function advance(
  task: TaskDefinition,
  state: LadderState,
  correct: boolean,
): LadderStep {
  const { minDifficulty, maxDifficulty, stepUpAfter, stepDownAfter, maxTrials } =
    task.measurement;

  const trialsDone = state.trialsDone + 1;
  const finished = trialsDone >= maxTrials;

  if (correct) {
    const correctRun = state.correctRun + 1;
    if (correctRun >= stepUpAfter && state.difficulty < maxDifficulty) {
      const to = state.difficulty + 1;
      return {
        difficulty: to,
        // Reset, so the next level needs its own streak rather than inheriting this one.
        correctRun: 0,
        errorRun: 0,
        trialsDone,
        change: { from: state.difficulty, to, reason: "correct_streak" },
        finished,
      };
    }
    /**
     * At the ceiling, a streak stops counting up and the child simply keeps playing.
     *
     * The old rule ended the task here with `reason: "ceiling"`. For a child who has finished a
     * game, being thrown out of it is a strange reward — and `maxTrials` already bounds the sitting,
     * so there is nothing left for an early exit to protect.
     */
    return {
      difficulty: state.difficulty,
      correctRun: state.difficulty >= maxDifficulty ? 0 : correctRun,
      errorRun: 0,
      trialsDone,
      change: null,
      finished,
    };
  }

  const errorRun = state.errorRun + 1;
  if (errorRun >= stepDownAfter && state.difficulty > minDifficulty) {
    const to = state.difficulty - 1;
    return {
      difficulty: to,
      correctRun: 0,
      errorRun: 0,
      trialsDone,
      change: { from: state.difficulty, to, reason: "error" },
      finished,
    };
  }

  /**
   * At the floor, errors accumulate and nothing happens.
   *
   * There is nowhere easier to go, and ending the sitting was the old behaviour this replaced. A
   * child who cannot do the easiest level of a game plays out their twelve trials and the record
   * says so plainly — which is what the teacher needs to see, and is not the same as the game
   * deciding they have had enough.
   */
  return {
    difficulty: state.difficulty,
    correctRun: 0,
    errorRun,
    trialsDone,
    change: null,
    finished,
  };
}
